
import Vue from "vue";
import { NewPublicPlacesApiFunc, NewWebsitesApiFunc, NewToiletsApiFunc, setAccessToken } from "@/api";
import { Website, PublicPlace, Toilet } from "@/models";

import WebsitePublicPlaceEditList, { EditOption } from "./components/WebsitePublicPlaceEditList.vue";
import TheTemplate from "../../TheNoSidebarTemplate.vue";
import BaseCloseButton from "@/components/BaseCloseButton.vue";
import BaseSubmitButton from "@/components/BaseSubmitButton.vue";
import BaseCancelButton from "@/components/BaseCancelButton.vue";
import Pager from "@/components/BasePager.vue";

export type DataType = {
  website?: Website;
  toilets: Array<Toilet>;
  selectedPublicPlaces: Array<PublicPlace>;
  publicPlaces: Array<EditOption>;
  totalPage: number;
  currentPage: number;
  slideOutToRight: boolean;
};

export default Vue.extend({
  name: "WebsitePublicPlaceEditListScreen",

  components: {
    TheTemplate,
    BaseCloseButton,
    WebsitePublicPlaceEditList,
    BaseSubmitButton,
    BaseCancelButton,
    Pager,
  },

  data(): DataType {
    return {
      website: undefined,
      toilets: [],
      selectedPublicPlaces: [],
      publicPlaces: [],
      totalPage: 0,
      currentPage: 0,
      slideOutToRight: false,
    };
  },

  computed: {
    websitePasscodeText(): string {
      return this.website?.passcode || "-";
    },

    websiteUseTenantIdPassText(): string {
      return this.website?.useTenantIdPass === true ? "必要" : "不要";
    },

    websiteUrlText(): string {
      return this.website?.url || "";
    },
  },

  watch: {
    selectedPublicPlaces: function(n: Array<PublicPlace>) {
      if (!this.toilets) {
        return;
      }
      this.publicPlaces = this.toilets.map(
        (toilet: Toilet): EditOption => {
          return {
            toiletId: toilet.id || "",
            checked: !!n.find(pp => pp.toiletId == toilet.id),
          };
        },
      );
    },

    toilets: function(n: Array<Toilet>) {
      if (!this.selectedPublicPlaces) {
        return;
      }
      this.publicPlaces = n.map(
        (toilet: Toilet): EditOption => {
          return {
            toiletId: toilet.id || "",
            checked: !!this.selectedPublicPlaces.find(pp => pp.toiletId == toilet.id),
          };
        },
      );
    },
  },

  mounted() {
    this.getWebsite();
    this.fetchWebsitePublicPlaces();
    this.fetchToilets();
  },

  methods: {
    async getWebsite(): Promise<void> {
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      const websiteId = this.$router.currentRoute.params.websiteId;
      setAccessToken(this.$auth.accessToken);
      const { data, err } = await NewWebsitesApiFunc().getWebsite(companyId, websiteId);

      if (err != null) {
        this.$handleApiError(err, this.getWebsite);
        return;
      }

      this.website = data;
    },

    async fetchWebsitePublicPlaces(): Promise<void> {
      const websiteId = this.$route.params.websiteId;
      const filterString: string = JSON.stringify({});
      setAccessToken(this.$auth.accessToken);
      const { data, err } = await NewPublicPlacesApiFunc().listPublicPlaces(
        this.$auth.companyId || "",
        websiteId,
        filterString,
        -1, // get all the public places
      );

      if (err != null)
        return this.$handleApiError(err, () => {
          this.fetchWebsitePublicPlaces();
        });

      this.selectedPublicPlaces = data.list;
    },

    async fetchToilets(): Promise<void> {
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      const filterString: string = JSON.stringify({ q: "", status: "" });
      setAccessToken(this.$auth.accessToken);
      const { data, err } = await NewToiletsApiFunc().listToilets(
        companyId,
        filterString,
        -1, // get all toilets
      );

      if (err != null)
        return this.$handleApiError(err, () => {
          this.fetchToilets();
        });

      this.toilets = data.list;
      this.currentPage = data.currentPage;
      this.totalPage = data.totalPage;
    },

    async updatePublicPlaces(): Promise<void> {
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      this.$setLoading(true);

      const publicPlaces = this.publicPlaces
        .map((option: EditOption, index: number): PublicPlace | undefined => {
          if (!option.checked) {
            return undefined;
          }
          return new PublicPlace({
            websiteId: this.website?.id,
            toiletId: option.toiletId,
            sort: index,
            toilet: this.toilets.find(t => t.id == option.toiletId),
          });
        })
        .filter(e => !!e) as PublicPlace[];
      const websiteId = this.$route.params.websiteId;
      setAccessToken(this.$auth.accessToken);
      const { err } = await NewPublicPlacesApiFunc().updatePublicPlaces(companyId, websiteId, publicPlaces);

      if (err != null) {
        this.$handleApiError(err, () => {
          this.updatePublicPlaces();
        });
        this.$setLoading(false);
        return;
      }
      this.$setLoading(false);
      this.$setChecked(true);
      this.$afterSuccessRequest(() => {
        this.$router.push({
          name: "company-website-public-places",
          params: {
            updated: "true",
            websiteId: this.website?.id as string,
          },
        });
      }, 2);
    },

    afterLeave(): void {
      this.$setChecked(false);
    },
    changePage(page: number): void {
      this.currentPage = page;
      // this.fetchWebsitePublicPlaces(page)
      // this.fetchToilets(page)
    },
  },
});
